import React from 'react';
import { useTranslation } from 'react-i18next';

import { MOBILE } from '../../mobile.tokens';
import {
  Root,
  Scrim,
  Row,
  BackButton,
  SkipButton,
  SkipLabel,
  Segments,
  Segment,
  Title,
} from './MobileHeader.styles';

export type MobileHeaderProps = {
  /** 0-based active step (0 design … 3 finish). */
  activeStep?: number;
  /** Gradient screen title (omitted when empty). */
  title?: string;
  /** Override the gradient used for the title text. */
  titleGradient?: string;
  /** Show the back arrow (left) when provided. */
  onBack?: () => void;
  /** Show the Skip link (right) when provided. */
  onSkip?: () => void;
  /** Black→transparent gradient band behind the header. */
  scrim?: boolean;
  /** Float over the content so it scrolls underneath. */
  overlay?: boolean;
};

const BackIcon = (): React.ReactElement => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M16.67 10H3.33M8.33 5L3.33 10L8.33 15"
      stroke={MOBILE.white}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const segState = (i: number, active: number): 'active' | 'done' | 'idle' =>
  i === active ? 'active' : i < active ? 'done' : 'idle';

export const ProgressSegments = ({ activeStep }: { activeStep: number }): React.ReactElement => (
  <Segments>
    {[0, 1, 2, 3].map((i) => (
      <Segment key={i} $state={segState(i, activeStep)} />
    ))}
  </Segments>
);

/**
 * Mobile chrome header: optional back arrow, 4-segment progress indicator,
 * optional Skip link, and the gradient screen title.
 */
export const MobileHeader = ({
  activeStep = 0,
  title,
  titleGradient = MOBILE.titleGradient,
  onBack,
  onSkip,
  scrim = false,
  overlay = false,
}: MobileHeaderProps): React.ReactElement => {
  const { t } = useTranslation();
  // When no title is supplied the default is the (translated) "Customize website".
  // An explicit empty string ('') deliberately hides the title (finish step).
  const resolvedTitle = title === undefined ? t('Customize website') : title;
  return (
    <Root $overlay={overlay}>
      {scrim ? <Scrim /> : null}

      <Row>
        {onBack ? (
          <BackButton type="button" data-ga-id="cfg-mobile-header-back" onClick={onBack} aria-label={t('Back')}>
            <BackIcon />
          </BackButton>
        ) : null}
        <ProgressSegments activeStep={activeStep} />
        {onSkip ? (
          <SkipButton type="button" data-ga-id="cfg-mobile-header-skip" onClick={onSkip} aria-label={t('Skip')}>
            <SkipLabel text="Skip" />
          </SkipButton>
        ) : null}
      </Row>

      {resolvedTitle ? <Title text={resolvedTitle} tag="paragraph" $gradient={titleGradient} /> : null}
    </Root>
  );
};

export default MobileHeader;
